import React from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Select, Toggle, type SelectOption } from '@/nova/components';

import { legalPresetText, type BannerKitCatalog, type LegalLine, type LegalPreset } from '../../banner-kit-api';
import { miniGameLegal } from '../../mini-game-copy';
import * as S from './BannerKitComposer.styles';

export interface LegalLineFieldProps {
  legal: LegalLine;
  catalog: BannerKitCatalog | null;
  /** The brand's bare host, filled into a preset's `{domain}`. */
  domain: string;
  disabled?: boolean;
  /** ALREADY TRANSLATED: where the line is drawn, shown under the text (rendered raw). Omitted = the kits' line
   *  ("drawn onto every size when you download"). */
  hint?: string;
  /** A mini-game's line: always on and always a market preset's own text. The owner picks the market only (no
   *  toggle, no Custom, no text); every change emits `{ enabled: true, preset, text: '' }`. */
  presetOnly?: boolean;
  onChange: (next: LegalLine) => void;
}

const isLegalPreset = (value: string): value is LegalPreset =>
  value === 'uk_eu' || value === 'us' || value === 'br' || value === 'custom';

/** A market preset's label, translated (the market picker's options, a closed section's summary). Pure. */
export const legalPresetLabel = (preset: LegalPreset, t: (key: string) => string): string => {
  switch (preset) {
    case 'us':
      return t('United States');
    case 'br':
      return t('Brazil');
    case 'custom':
      return t('Custom');
    default:
      return t('UK and EU');
  }
};

/** The line a legal setting draws: the owner's own text, else the preset's with the domain filled in. Pure. */
export const legalLineText = (legal: LegalLine, catalog: BannerKitCatalog | null, domain: string): string =>
  legal.text || legalPresetText(catalog, legal.preset, domain);

/**
 * The kit's legal line: drawn by code onto every exported size (never painted into the art, so changing it never
 * re-renders anything). On by default with the UK/EU preset; the owner can switch preset, edit the text, or turn it
 * off (their responsibility). An empty text follows the preset. `presetOnly` (mini-games): the line is always on and
 * reads its market preset's own text, shown read-only; only the market is picked.
 */
export const LegalLineField = ({
  legal,
  catalog,
  domain,
  disabled = false,
  hint,
  presetOnly = false,
  onChange,
}: LegalLineFieldProps): React.ReactElement => {
  const { t } = useTranslation();
  const shown = legalLineText(legal, catalog, domain);

  const presetOptions: SelectOption[] = [
    { value: 'uk_eu', label: legalPresetLabel('uk_eu', t) },
    { value: 'us', label: legalPresetLabel('us', t) },
    { value: 'br', label: legalPresetLabel('br', t) },
    { value: 'custom', label: legalPresetLabel('custom', t) },
  ];

  if (presetOnly) {
    // The document may still hold the kits' Custom choice (or a line turned off): the composer locks it at every use
    // (always on, the preset's own text), so the picker and the line on screen show what is drawn.
    const { preset } = miniGameLegal(legal);
    const line = legalPresetText(catalog, preset, domain);
    return (
      <>
        <Select
          label={t('Market')}
          value={preset}
          onChange={(value) => {
            if (disabled || !isLegalPreset(value) || value === 'custom') return;
            onChange({ enabled: true, preset: value, text: '' });
          }}
          options={presetOptions.filter((o) => o.value !== 'custom')}
          disabled={disabled}
        />
        {line ? <S.CopySub raw text={line} /> : null}
        <S.Hint text="Always shown on a mini-game, with your market's standard wording." />
        {hint ? <S.Hint raw text={hint} /> : null}
      </>
    );
  }

  const onPreset = (value: string): void => {
    if (disabled || !isLegalPreset(value)) return;
    // A preset brings its own line (text '' follows it); Custom starts from the line on screen so the owner edits it.
    onChange({ ...legal, preset: value, text: value === 'custom' ? shown : '' });
  };

  return (
    <>
      <S.ToggleRow>
        <S.ToggleLabel text="Show a legal line" />
        <Toggle
          on={legal.enabled}
          label={t('Show a legal line')}
          disabled={disabled}
          onToggle={() => {
            if (!disabled) onChange({ ...legal, enabled: !legal.enabled });
          }}
        />
      </S.ToggleRow>
      {legal.enabled ? (
        <>
          <Select
            label={t('Market')}
            value={legal.preset}
            onChange={onPreset}
            options={presetOptions}
            disabled={disabled}
          />
          <Field
            label={t('Legal text')}
            value={shown}
            onChange={(value) => {
              if (!disabled) onChange({ ...legal, text: value });
            }}
            placeholder={t('Write your legal line')}
            disabled={disabled}
          />
          {hint ? (
            <S.Hint raw text={hint} />
          ) : (
            <S.Hint text="Drawn onto every size when you download, so changing it never re-renders the art." />
          )}
        </>
      ) : (
        <S.Hint text="No legal line is drawn. You are responsible for what your market requires." />
      )}
    </>
  );
};

export default LegalLineField;
